import { FC } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {CheckTimeIcon} from "src/assets/Icons";
import { formatDate as formatDateLocalized } from "src/utility";

const DATE_FORMAT = "MMM DD, YYYY";

export interface StayDatesProps {
  checkin?: dayjs.Dayjs | string | null;
  checkout?: dayjs.Dayjs | string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  numberOfNights?: number;
  variant?: "compact" | "timeline";
}

function formatDate(date: dayjs.Dayjs | string | null | undefined): string | null {
  if (!date) return null;
  return formatDateLocalized(date, DATE_FORMAT);
}

const StayNode: FC<{
  label: string;
  dateLabel: string | null;
  timeLabel: string | null;
  align?: "left" | "right";
}> = ({
  label,
  dateLabel,
  timeLabel,
  align = "left",
}) => {
  const alignmentClass = align === "right" ? "flex-row-reverse text-right" : "";
  const textAlignClass = align === "right" ? "items-end" : "";

  return (
    <div className={`flex items-center gap-4 ${alignmentClass}`}>
      <div className="text-font-active bg-background-primary-light flex h-12 w-12 items-center justify-center rounded-lg">
        <CheckTimeIcon />
      </div>
      <div className={`flex flex-col gap-1 ${textAlignClass}`}>
        <span className="text-font-active/80 text-[10px] font-semibold uppercase tracking-[1px]">{label}</span>
        <span className="font-inter text-[16px] font-semibold text-font-subtle">{dateLabel ?? "--"}</span>
        <span className="font-inter text-[14px] font-semibold text-font-subtle">{timeLabel ?? "--"}</span>
      </div>
    </div>
  );
};

const StayDates: FC<StayDatesProps> = ({
  checkin,
  checkout,
  numberOfNights = 0,
}) => {
  const { t } = useTranslation();
  const checkinDate = formatDate(checkin);
  const checkoutDate = formatDate(checkout);
  const nightsLabel = numberOfNights === 1 ? t("Hotels.common.oneNight") : t("Hotels.common.nightsCount", { count: numberOfNights });
  return (
    <div className="flex w-full items-center justify-between p-4">
      <div className="flex flex-col gap-1">
        <p className="font-inter text-font-subtle text-xs font-bold uppercase tracking-wide">{t("Hotels.common.checkIn")}</p>
        <p className="font-inter text-font-default text-base">{checkinDate ?? "—"}</p>
      </div>

      {numberOfNights > 0 && (
        <div className="bg-background-primary-lighter rounded-full px-3 py-1">
          <p className="font-inter text-font-active text-[10px] font-bold">{nightsLabel}</p>
        </div>
      )}

      <div className="flex flex-col items-end gap-1 text-right">
        <p className="font-inter text-font-subtle text-xs font-bold uppercase tracking-wide">{t("Hotels.common.checkOut")}</p>
        <p className="font-inter text-font-default text-base">{checkoutDate ?? "—"}</p>
      </div>
    </div>
  );
};

export default StayDates;